import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Database, Download, Upload, Trash2, Clock, 
  CheckCircle, XCircle, AlertTriangle, RefreshCw, Settings,
  HardDrive, Calendar, Play, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import SelectiveBackupSection from './SelectiveBackupSection';

const BackupRestore = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [backupLogs, setBackupLogs] = useState([]);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: true,
    frequency: 'daily',
    time_utc: '02:00',
    retention_days: 30
  });
  
  // Job tracking
  const [activeJob, setActiveJob] = useState(null);
  const [jobPolling, setJobPolling] = useState(null);
  
  // Dialogs
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  useEffect(() => {
    fetchData();
    return () => {
      if (jobPolling) clearInterval(jobPolling);
    };
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statusRes, backupsRes, logsRes, scheduleRes] = await Promise.all([
        apiClient.get('/backup/status'),
        apiClient.get('/backup/list'),
        apiClient.get('/backup/logs?limit=20'),
        apiClient.get('/backup/schedule')
      ]);
      
      setServiceStatus(statusRes.data);
      setBackups(backupsRes.data.backups || []);
      setBackupLogs(logsRes.data.logs || []);
      setScheduleConfig(scheduleRes.data);
    } catch (error) {
      console.error('Error fetching backup data:', error);
      toast.error('Failed to load backup data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      const response = await apiClient.post('/backup/create');
      const jobId = response.data.job_id;
      
      setActiveJob({
        id: jobId,
        type: 'backup',
        status: 'in_progress'
      });
      
      toast.info('Backup started...');
      
      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await apiClient.get(`/backup/job/${jobId}`);
          const status = statusRes.data.status;
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.success('Backup completed successfully!');
            fetchData();
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.error(`Backup failed: ${statusRes.data.error}`);
          }
        } catch (e) {
          console.error('Error polling job status:', e);
        }
      }, 3000);
      
      setJobPolling(pollInterval);
      
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to start backup');
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      const response = await apiClient.get(`/backup/download/${backupId}`);
      const downloadUrl = response.data.download_url;
      
      // Open download in new tab
      window.open(downloadUrl, '_blank');
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to download backup');
    }
  };

  const handleRestoreBackup = async () => {
    if (restoreConfirm !== 'RESTORE') {
      toast.error('Please type RESTORE to confirm');
      return;
    }
    
    try {
      const response = await apiClient.post('/backup/restore', {
        backup_id: selectedBackup.backup_id,
        drop_existing: true,
        confirm: true
      });
      
      const jobId = response.data.job_id;
      
      setShowRestoreDialog(false);
      setRestoreConfirm('');
      setSelectedBackup(null);
      
      setActiveJob({
        id: jobId,
        type: 'restore',
        status: 'in_progress'
      });
      
      toast.warning('Restore in progress... Do not close this page!');
      
      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await apiClient.get(`/backup/job/${jobId}`);
          const status = statusRes.data.status;
          const progress = statusRes.data.progress;
          
          setActiveJob(prev => ({
            ...prev,
            progress: progress || null
          }));
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.success('Database restored successfully! Page will reload...');
            setTimeout(() => window.location.reload(), 2000);
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.error(`Restore failed: ${statusRes.data.error || statusRes.data.progress?.message || 'Unknown error'}`);
          }
        } catch (e) {
          console.error('Error polling job status:', e);
        }
      }, 2000);
      
      setJobPolling(pollInterval);
      
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm(`Delete backup ${backupId}? This cannot be undone.`)) return;
    
    try {
      await apiClient.delete(`/backup/${backupId}`);
      toast.success('Backup deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      await apiClient.post('/backup/schedule', scheduleConfig);
      toast.success('Backup schedule updated');
      setShowSettingsDialog(false);
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Delete backups older than 30 days?')) return;
    
    try {
      const response = await apiClient.post('/backup/cleanup');
      toast.success(`Cleanup complete. Deleted ${response.data.deleted} old backups.`);
      fetchData();
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    }
  };

  const handleUploadAndRestore = async () => {
    if (!uploadFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    if (!uploadFile.name.endsWith('.tar.gz') && !uploadFile.name.endsWith('.tgz')) {
      toast.error('Please select a .tar.gz backup file');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
      const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);
      
      // Step 1: Init upload
      const initForm = new FormData();
      initForm.append('filename', uploadFile.name);
      initForm.append('total_size', uploadFile.size);
      initForm.append('total_chunks', totalChunks);
      
      const initRes = await apiClient.post('/backup/upload/init', initForm);
      const uploadId = initRes.data.upload_id;
      
      // Step 2: Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, uploadFile.size);
        const blob = uploadFile.slice(start, end);
        
        const chunkForm = new FormData();
        chunkForm.append('upload_id', uploadId);
        chunkForm.append('chunk_index', i);
        chunkForm.append('chunk', blob, `chunk_${i}`);
        
        await apiClient.post('/backup/upload/chunk', chunkForm);
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 60)); // Upload is 60% of total
      }
      
      // Step 3: Complete upload (assemble chunks)
      setUploadProgress(65);
      const completeForm = new FormData();
      completeForm.append('upload_id', uploadId);
      await apiClient.post('/backup/upload/complete', completeForm);
      
      setUploadProgress(70);
      setShowUploadDialog(false);
      
      // Step 4: Start restore
      const restoreForm = new FormData();
      restoreForm.append('upload_id', uploadId);
      const restoreRes = await apiClient.post('/backup/restore-from-upload', restoreForm);
      const jobId = restoreRes.data.job_id;
      
      setActiveJob({
        id: jobId,
        type: 'restore',
        status: 'in_progress'
      });
      
      toast.warning('Restore from uploaded file in progress... Do not close this page!');
      
      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await apiClient.get(`/backup/job/${jobId}`);
          const status = statusRes.data.status;
          const progress = statusRes.data.progress;
          
          setActiveJob(prev => ({
            ...prev,
            progress: progress || null
          }));
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.success('Database restored successfully from uploaded file! Page will reload...');
            setTimeout(() => window.location.reload(), 2000);
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setActiveJob(null);
            toast.error(`Restore failed: ${statusRes.data.error || statusRes.data.progress?.message || 'Unknown error'}`);
          }
        } catch (e) {
          console.error('Error polling job status:', e);
        }
      }, 2000);
      
      setJobPolling(pollInterval);
      
    } catch (error) {
      console.error('Error uploading backup:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadFile(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="backup-restore-page">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <Database className="w-6 h-6" />
                Backup & Restore
              </h1>
              <p className="text-sm text-[#9CA3AF]">Database backup management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowSettingsDialog(true)} variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Active Job Banner */}
        {activeJob && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-700">
                    {activeJob.type === 'backup' ? 'Creating backup...' : 'Restoring database...'}
                  </p>
                  {activeJob.progress ? (
                    <div className="mt-1">
                      <p className="text-sm text-blue-600">{activeJob.progress.message}</p>
                      {activeJob.progress.percent > 0 && (
                        <div className="mt-2">
                          <div className="w-full bg-blue-200 rounded-full h-2.5">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(activeJob.progress.percent, 100)}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-1 text-xs text-blue-500">
                            <span>{activeJob.progress.percent}%</span>
                            {activeJob.progress.total_collections > 0 && (
                              <span>
                                {activeJob.progress.collections_done}/{activeJob.progress.total_collections} collections 
                                {activeJob.progress.total_documents_restored > 0 && ` | ${activeJob.progress.total_documents_restored.toLocaleString()} docs`}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-blue-600">Please wait and do not close this page</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <HardDrive className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Backups</p>
                  <p className="text-2xl font-bold">{backups.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Service Status</p>
                  <p className={`text-lg font-semibold ${serviceStatus?.initialized ? 'text-green-600' : 'text-red-600'}`}>
                    {serviceStatus?.initialized ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Auto Backup</p>
                  <p className={`text-lg font-semibold ${scheduleConfig.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                    {scheduleConfig.enabled ? `Daily @ ${scheduleConfig.time_utc} UTC` : 'Disabled'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Retention</p>
                  <p className="text-lg font-semibold">{scheduleConfig.retention_days} days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={handleCreateBackup} 
                disabled={activeJob}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-backup-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                Create Backup Now
              </Button>
              <Button 
                onClick={() => setShowUploadDialog(true)} 
                disabled={activeJob}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="upload-restore-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload & Restore from File
              </Button>
              <Button 
                onClick={handleCleanup} 
                variant="outline"
                disabled={activeJob}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cleanup Old Backups
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Backups Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              Available Backups
            </CardTitle>
            <CardDescription>
              All backups stored in Google Cloud Storage
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-500">Loading backups...</p>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-8">
                <Database className="w-12 h-12 mx-auto text-gray-300" />
                <p className="mt-2 text-gray-500">No backups found</p>
                <p className="text-sm text-gray-400">Create your first backup using the button above</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Backup ID</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.backup_id}>
                        <TableCell className="font-mono text-sm">{backup.backup_id}</TableCell>
                        <TableCell>{formatDate(backup.created_at)}</TableCell>
                        <TableCell className="text-right">{formatBytes(backup.size_bytes)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleDownloadBackup(backup.backup_id)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-800"
                              data-testid={`download-${backup.backup_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedBackup(backup);
                                setShowRestoreDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-orange-600 hover:text-orange-800"
                              disabled={activeJob}
                              data-testid={`restore-${backup.backup_id}`}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteBackup(backup.backup_id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-800"
                              disabled={activeJob}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selective Collection Backup */}
        <SelectiveBackupSection
          disabled={!!activeJob}
          onChanged={fetchData}
        />

        {/* Recent Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {backupLogs.length === 0 ? (
              <p className="text-center py-4 text-gray-500">No backup activity yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {backupLogs.map((log, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      log.status === 'completed' ? 'bg-green-50' : 
                      log.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {log.status === 'completed' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : log.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <Clock className="w-4 h-4 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {log.operation || log.trigger_type || 'Backup'} 
                          {log.backup_id && <span className="text-gray-500 ml-2">({log.backup_id})</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {log.triggered_by} • {formatDate(log.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {log.size_bytes && (
                        <p className="text-sm text-gray-600">{formatBytes(log.size_bytes)}</p>
                      )}
                      {log.error && (
                        <p className="text-xs text-red-600 max-w-xs truncate">{log.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Restore Database
            </DialogTitle>
            <DialogDescription>
              This will replace your current database with the backup. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>Backup:</strong> {selectedBackup?.backup_id}
              </p>
              <p className="text-sm text-orange-800">
                <strong>Created:</strong> {formatDate(selectedBackup?.created_at)}
              </p>
              <p className="text-sm text-orange-800">
                <strong>Size:</strong> {formatBytes(selectedBackup?.size_bytes)}
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold text-red-600">
                Type "RESTORE" to confirm:
              </Label>
              <Input
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value)}
                placeholder="RESTORE"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRestoreDialog(false);
              setRestoreConfirm('');
              setSelectedBackup(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreBackup}
              disabled={restoreConfirm !== 'RESTORE'}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Restore Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Backup Settings
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">Enable Automatic Backups</Label>
                <p className="text-sm text-gray-500">Run daily backups automatically</p>
              </div>
              <Switch
                checked={scheduleConfig.enabled}
                onCheckedChange={(checked) => setScheduleConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
            
            <div>
              <Label className="font-semibold">Backup Time (UTC)</Label>
              <Input
                type="time"
                value={scheduleConfig.time_utc}
                onChange={(e) => setScheduleConfig(prev => ({ ...prev, time_utc: e.target.value }))}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: Late night hours (e.g., 02:00) for minimal impact
              </p>
            </div>
            
            <div>
              <Label className="font-semibold">Retention Period (days)</Label>
              <Input
                type="number"
                min="7"
                max="365"
                value={scheduleConfig.retention_days}
                onChange={(e) => setScheduleConfig(prev => ({ ...prev, retention_days: parseInt(e.target.value) || 30 }))}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Backups older than this will be automatically deleted
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSchedule} className="bg-[#2D5F4F] hover:bg-[#234A3D]">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Upload & Restore Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!isUploading) setShowUploadDialog(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Upload className="w-5 h-5" />
              Upload Backup File
            </DialogTitle>
            <DialogDescription>
              Upload a .tar.gz backup file from your local machine to restore the database.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
              <input
                type="file"
                accept=".tar.gz,.tgz"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                id="backup-file-input"
                disabled={isUploading}
                data-testid="backup-file-input"
              />
              <label htmlFor="backup-file-input" className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {uploadFile ? uploadFile.name : 'Click to select a .tar.gz backup file'}
                </p>
                {uploadFile && (
                  <p className="text-xs text-gray-400 mt-1">
                    {(uploadFile.size / (1024 * 1024)).toFixed(1)} MB
                  </p>
                )}
              </label>
            </div>
            
            {isUploading && (
              <div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
            
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-xs text-orange-700">
                <strong>Warning:</strong> This will replace your entire database with the backup contents. 
                Make sure you have a recent backup before proceeding.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadFile(null); }} disabled={isUploading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadAndRestore}
              disabled={!uploadFile || isUploading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-upload-restore-btn"
            >
              {isUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Restore
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupRestore;
